(() => {
  if (!customElements.get("collection-tabs")) {
    class CollectionTabs extends HTMLElement {
      constructor() {
        super();
        this.tabs = this.querySelectorAll('.collection-main-selector');
        this.sections = Array.from(document.querySelectorAll('.tab-section'));
        this.observer = null;
        this._onScroll = this._onScroll.bind(this);
        this._raf = null;
      }

      connectedCallback() {
        this.initObserver();
        window.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._onScroll, { passive: true });
        // initial sync
        this._onScroll();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        window.removeEventListener('scroll', this._onScroll);
        window.removeEventListener('resize', this._onScroll);
        if (this._raf) cancelAnimationFrame(this._raf);
      }

      // Re-scan sections if DOM changes (you can call this after AJAX/injections too)
      reinitObserver() {
        if (this.observer) this.observer.disconnect();
        this.sections = Array.from(document.querySelectorAll('.tab-section'));
        this.initObserver(); // reattach for future mutations (optional)
        this._onScroll();    // resync active tab
      }

      initObserver() {
        // Optional: observe each section for presence changes, but don't rely on its ratios
        this.observer = new IntersectionObserver(() => {
          // When anything changes, resync via scroll logic next frame
          this._onScroll();
        }, {
          root: null,
          // More forgiving margins on mobile; avoid negative 50% band
          rootMargin: '0px 0px 0px 0px',
          threshold: [0, 0.25, 0.5, 0.75, 1]
        });

        this.sections.forEach(s => this.observer.observe(s));
      }

      _onScroll() {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = null;

          // Account for sticky header if you have one (read a CSS var if present)
          const headerH = parseInt(
            getComputedStyle(document.documentElement)
              .getPropertyValue('--section-header-height')
          ) || 0;

          // Choose a reference line ~35% down the viewport, below the header
          const viewportH = window.innerHeight || document.documentElement.clientHeight;
          const refY = window.scrollY + headerH + (viewportH - headerH) * 0.35;

          // Find the section whose [top, bottom) contains refY
          let best = null;
          for (const section of this.sections) {
            const rect = section.getBoundingClientRect();
            const top = window.scrollY + rect.top;
            const bottom = top + rect.height;
            if (refY >= top && refY < bottom) {
              best = section;
              break;
            }
          }

          // Fallback: if none contains refY (very small sections), pick the nearest above
          if (!best) {
            const below = this.sections
              .map(s => ({ s, top: window.scrollY + s.getBoundingClientRect().top }))
              .filter(o => o.top <= refY)
              .sort((a, b) => b.top - a.top);
            best = (below[0] && below[0].s) || this.sections[0];
          }

          if (!best) return;

          const id = best.getAttribute('id');
          if (!id) return;

          const activeTab = this.querySelector(`.collection-main-selector[href="#${CSS.escape(id)}"]`);
          if (!activeTab) return;

          // Update classes only if changed (avoids layout thrash)
          if (!activeTab.classList.contains('active')) {
            this.tabs.forEach(tab => tab.classList.remove('active'));
            activeTab.classList.add('active');
          }
        });
      }
    }

    customElements.define("collection-tabs", CollectionTabs);
  }
})();